/**
 * Video Generation 模块配置常量
 */
import i18n from '@/i18n/config';

// 分页配置
export const PAGINATION = {
  PAGE_SIZE: 10,
  INITIAL_PAGE: 1,
} as const;

// 默认值配置
export const DEFAULTS = {
  DURATION: '5 s',
  RESOLUTION: '自动匹配',
  GENERATION_COUNT: 1,
  REFERENCE_TAB: '参考图' as const,
} as const;

// 创建类型映射
export const CREATION_TYPE_MAP = {
  全部: [2, 3],
  图片: [2],
  视频: [3],
} as const;

// 示例图片前缀
export const EXAMPLE_IMAGE_PREFIX = 'example_';

// 模型选择限制配置
export const MODEL_SELECTION_CONSTRAINTS = {
  // 最大选择模型数量
  MAX_SELECTED_MODELS: 2,
} as const;

// 错误消息
export const ERROR_MESSAGES = {
  get UPLOAD_FAILED()           { return i18n.t('imageCreation.uploadFailed'); },
  get GENERATION_FAILED()       { return i18n.t('imageCreation.genFailed'); },
  get NETWORK_ERROR()           { return i18n.t('imageCreation.networkFailed'); },
  get UNKNOWN_ERROR()           { return i18n.t('imageCreation.unknownError'); },
  get INSUFFICIENT_PARAMS()     { return i18n.t('imageCreation.paramInsufficient'); },
  get KLING_2_1_NO_TAIL_FRAME() { return i18n.t('imageCreation.kling21NoTailFrame'); },
  get MAX_MODELS_SELECTED()     { return i18n.t('imageCreation.maxTwoModelsError'); },
};

// 成功消息
export const SUCCESS_MESSAGES = {
  get REFERENCE_TASK_CREATED()       { return i18n.t('imageCreation.videoRefTaskCreated'); },
  get KEYFRAME_TASK_CREATED()        { return i18n.t('imageCreation.videoKeyframeTaskCreated'); },
  get REGENERATE_TASK_CREATED()      { return i18n.t('imageCreation.videoRegenTaskCreated'); },
  get BATCH_REGENERATE_TASK_CREATED(){ return i18n.t('imageCreation.videoBatchRegenTaskCreated'); },
};

// 存储键名
export const STORAGE_KEYS = {
  ACTIVE_TAB: 'creationActiveTab',
} as const;

// 文件类型
export const FILE_TYPES = {
  IMAGE: '.jpg,.jpeg,.png',
} as const;

// 图片上传限制
export const IMAGE_UPLOAD_CONSTRAINTS = {
  MAX_SIZE: '20MB',
  MIN_RESOLUTION: '300*300px',
  ASPECT_RATIO: '1:2.5 ~ 2.5:1',
  FORMATS: '.jpg/.jpeg/.png',
} as const;

// 任务类型
export const TASK_TYPES = {
  IMAGE: 2,
  VIDEO: 3,
} as const;

// 视频生成模型ID常量
export const VIDEO_MODEL_IDS = {
  // 可灵 1.6
  KLING_V1_6: '01JVKEMRMVV3M3YVQ9H832VYY3',
  // 可灵 2.1 - 仅支持图生视频 5s、10s，不可上传尾帧
  KLING_V2_1: '01K1321KC75YYHAXQTW0RV83M5',
  // 可灵 3.0
  KLING_V3: '01KK88SZ0K8N1GBR2XYWYWE291',
  // 可灵 3.0 Omni
  KLING_V3_OMNI: '01KKJDY556DWXJJD60T92PA6AF',
  // Wan2.1 - 仅支持图生视频 5s，不可上传尾帧
  WAN2_1: '01K1G3KD3DY0TS20QY78WQ80RY',
  // Wan2.1 - 仅支持图生视频 5s，不可上传尾帧
  WAN2_2: '01K5THVBNHF0QDSG0Y3C5PNW00',
  // 即梦 3.0 - 仅支持图生视频 5s、10s，支持首尾帧上传
  JIMENG_V3: '01K1AARPXHVDP0MD24RCFTGKAJ',
  // 即梦 3.0 - 仅支持图生视频 5s、10s，支持首尾帧上传
  HAILUO_02: '01K5THTB4CDG3D0E26BZR6P1VC',
  // MiniMax H3 - 多模态独占模型（首尾帧、音视频参考、文生/图生视频）
  MINIMAX_H3: '01KZ2PZ01A08NF3RR0PEXM624C',
  SORA_2: '01K8HSKZN2ZBMWCJS1E8FZ9KE0',
  SORA_2_PRO: '01K8HSJS0Y0ZN2CEY41CEMGVVJ',
  // Seedance 2.0 - 独占模型
  SEEDANCE_2_0: '01KN8PXW9FSJ6QQA6KRT4GVDY4',
  // Seedance 2.0 Fast - 即梦 doubao-seedance-2-0-fast-260128，行为与 Seedance 2.0 一致（首尾帧 / 多模态参考 / 真人视频 / 校验限制）
  SEEDANCE_2_0_FAST: '01M21ZPA48AFCXSNS39FKKQ8FJ',
  // Seedance 2.5 - 多模态独占模型（行为与 Seedance 2.0 一致）
  SEEDANCE_2_5: '01KZDKGGERX2MFRYEYKP0RDN3N',
  // Wan2.7 - 独占模型 + 多模态参考（音视频同步、视频续写、首尾帧）
  WAN2_7: '01KYPMN0SJMX79NH2SV9RE2CCF',
  // Wan3.0 家族 - 多模态参考新增文档类型（仅这些模型多模态模式下展示 +文档 入口）
  WAN3_0: '01M1ZGR99NQPEGSQ75PQ3V7E8F', // wan3.0-video
  WAN3_0_PRIME: '01M1ZQZ0A1YC40TCZ2E86YD5NM', // wan3.0-video-prime
  // Veo 3.1 - 支持@参考图功能
  VEO_3_1: '01KB24RHZ1F3S22DE3RGSJS8S8',
} as const;

// Wan3.0 家族模型 ID 列表（wan3.0-video / wan3.0-video-prime），多模态参考文档引用等能力对二者统一生效
export const WAN3_0_MODEL_IDS: string[] = [
  VIDEO_MODEL_IDS.WAN3_0, // wan3.0-video
  VIDEO_MODEL_IDS.WAN3_0_PRIME, // wan3.0-video-prime
];

// 单个模型 ID 是否属于 Wan3.0 家族
export const isWan30ModelId = (modelId: string): boolean =>
  WAN3_0_MODEL_IDS.includes(modelId);

// 选中模型列表中是否含 Wan3.0 家族模型
export const hasWan30Model = (selectedModels: string[]): boolean =>
  WAN3_0_MODEL_IDS.some(id => selectedModels.includes(id));

// Seedance 2.x 家族模型 ID 列表（Seedance 2.0 / 2.0 Fast / 2.5）
// 三者的多模态参考、首尾帧、真人视频、参考素材校验等能力一致，UI 与校验逻辑统一适用
export const SEEDANCE_2_MODEL_IDS: string[] = [
  VIDEO_MODEL_IDS.SEEDANCE_2_0,
  VIDEO_MODEL_IDS.SEEDANCE_2_0_FAST,
  VIDEO_MODEL_IDS.SEEDANCE_2_5,
];

// 单个模型 ID 是否属于 Seedance 2.x 家族
export const isSeedance2ModelId = (modelId: string): boolean =>
  SEEDANCE_2_MODEL_IDS.includes(modelId);

// 选中模型列表中是否含 Seedance 2.x 家族模型
export const hasSeedance2Model = (selectedModels: string[]): boolean =>
  SEEDANCE_2_MODEL_IDS.some(id => selectedModels.includes(id));

// 默认模型选择配置
export const DEFAULT_MODEL_SELECTION = {
  // 参考图模式默认模型
  REFERENCE_TAB_DEFAULT: [
    VIDEO_MODEL_IDS.KLING_V3, // 可灵 3.0
  ],
  // 首尾帧模式默认模型
  KEYFRAME_TAB_DEFAULT: [
    VIDEO_MODEL_IDS.KLING_V3, // 可灵 3.0
    VIDEO_MODEL_IDS.JIMENG_V3, // 即梦 3.0
  ],
} as const;

// Redux slice 专用配置
export const SLICE_MODEL_CONFIG = {
  // 可灵 3.0 模型ID
  KLING_16_ID: VIDEO_MODEL_IDS.KLING_V3,
  // 即梦 3.0 模型ID
  JIMENG_3_ID: VIDEO_MODEL_IDS.JIMENG_V3,
  // 海螺02 模型ID
  HAILUO_02_ID: VIDEO_MODEL_IDS.HAILUO_02,
  // MiniMax H3 模型ID
  MINIMAX_H3_ID: VIDEO_MODEL_IDS.MINIMAX_H3,
} as const;

// 模型限制配置
export const MODEL_CONSTRAINTS = {
  // 不支持10s时长的模型ID列表（精确匹配模型ID）
  NO_10S_MODEL_IDS: [
    VIDEO_MODEL_IDS.WAN2_1, // Wan2.1仅支持5s
    VIDEO_MODEL_IDS.WAN2_2, // Wan2.2仅支持5s
  ],

  // 可灵2.1模型ID (保持向后兼容)
  KLING_2_1_MODEL_ID: VIDEO_MODEL_IDS.KLING_V2_1,

  // 不支持尾帧功能的模型ID列表
  NO_TAIL_FRAME_MODEL_IDS: [
    VIDEO_MODEL_IDS.KLING_V2_1, // 可灵2.1模型
    VIDEO_MODEL_IDS.WAN2_1, // Wan2.1模型
    VIDEO_MODEL_IDS.WAN2_2, // Wan2.2模型
    VIDEO_MODEL_IDS.SORA_2,
    VIDEO_MODEL_IDS.SORA_2_PRO
  ],

  // 不支持仅上传尾帧（Tail-only）的模型ID列表
  // 例如：即梦3.0 需要至少首帧或首尾齐全，不支持只给尾帧
  NO_TAIL_ONLY_MODEL_IDS: [
    VIDEO_MODEL_IDS.JIMENG_V3, // 即梦 3.0 不支持仅尾帧
    VIDEO_MODEL_IDS.HAILUO_02, // 海螺02不支持仅尾帧
  ],

  // 仅支持5s时长的模型ID列表
  ONLY_5S_MODELS: [
    VIDEO_MODEL_IDS.WAN2_1, // Wan2.1仅支持5s
    VIDEO_MODEL_IDS.WAN2_2, // Wan2.2仅支持5s
  ],
  // 仅支持6s时长的模型ID列表
  ONLY_6S_MODELS: [
    VIDEO_MODEL_IDS.HAILUO_02, // 海螺02支持6s、10s，但其他模型不支持6s
  ],

  // 仅支持图生视频的模型ID列表（不支持文生视频）
  IMAGE_TO_VIDEO_ONLY_MODELS: [
    VIDEO_MODEL_IDS.KLING_V2_1, // 可灵2.1仅支持图生视频
    VIDEO_MODEL_IDS.WAN2_1, // Wan2.1仅支持图生视频
    VIDEO_MODEL_IDS.WAN2_2, // Wan2.2仅支持图生视频
    VIDEO_MODEL_IDS.JIMENG_V3, // 即梦3.0仅支持图生视频
    VIDEO_MODEL_IDS.HAILUO_02, // 海螺02仅支持图生视频
  ],
} as const;

// 模型检查工具函数
export const MODEL_UTILS = {
  /**
   * 检查是否为可灵2.1模型
   * @param model 模型信息对象
   * @returns 是否为可灵2.1模型
   */
  isKling21Model: (model: {
    model_display_name?: string;
    model_name?: string;
    model_id?: string;
  }) => {
    if (!model || !model.model_id) return false;
    return model.model_id === VIDEO_MODEL_IDS.KLING_V2_1;
  },

  /**
   * 检查是否为Wan2.1模型
   * @param model 模型信息对象
   * @returns 是否为Wan2.1模型
   */
  isWan21Model: (model: {
    model_display_name?: string;
    model_name?: string;
    model_id?: string;
  }) => {
    if (!model || !model.model_id) return false;
    return model.model_id === VIDEO_MODEL_IDS.WAN2_1;
  },

  /**
   * 检查是否为Wan2.2模型
   * @param model 模型信息对象
   * @returns 是否为Wan2.2模型
   */
  isWan22Model: (model: {
    model_display_name?: string;
    model_name?: string;
    model_id?: string;
  }) => {
    if (!model || !model.model_id) return false;
    return model.model_id === VIDEO_MODEL_IDS.WAN2_2;
  },

  /**
   * 检查是否为海螺02模型
   * @param model 模型信息对象
   * @returns 是否为海螺02模型
   */
  isHailuo02Model: (model: {
    model_display_name?: string;
    model_name?: string;
    model_id?: string;
  }) => {
    if (!model || !model.model_id) return false;
    return model.model_id === VIDEO_MODEL_IDS.HAILUO_02;
  },

  /**
   * 检查是否为 MiniMax H3 模型
   * @param model 模型信息对象
   * @returns 是否为 MiniMax H3 模型
   */
  isMiniMaxH3Model: (model: {
    model_display_name?: string;
    model_name?: string;
    model_id?: string;
  }) => {
    if (!model || !model.model_id) return false;
    return model.model_id === VIDEO_MODEL_IDS.MINIMAX_H3;
  },

  /**
   * 检查是否为即梦3.0模型
   * @param model 模型信息对象
   * @returns 是否为即梦3.0模型
   */
  isJimeng3Model: (model: {
    model_display_name?: string;
    model_name?: string;
    model_id?: string;
  }) => {
    if (!model || !model.model_id) return false;
    return model.model_id === VIDEO_MODEL_IDS.JIMENG_V3;
  },
  // 检查是否是sora
  isSoraModel: (selectedModels: any) => {
    let sora2 = VIDEO_MODEL_IDS.SORA_2
    let sora2_pro = VIDEO_MODEL_IDS.SORA_2_PRO
    if(selectedModels.includes(sora2)){
      return 'sora2'
    }else if(selectedModels.includes(sora2_pro)){
      return 'sora2_pro'
    }else{
      return ''
    }
  },
  /**
   * 检查模型是否支持尾帧功能
   * @param model 模型信息对象
   * @returns 是否支持尾帧功能
   */
  supportsTailFrame: (model: {
    model_display_name?: string;
    model_name?: string;
    model_id?: string;
  }) => {
    if (!model || !model.model_id) return true;
    return !MODEL_CONSTRAINTS.NO_TAIL_FRAME_MODEL_IDS.includes(model.model_id as any);
  },

  /**
   * 检查模型是否允许仅上传尾帧（tail-only）
   * @param model 模型信息对象
   * @returns 是否允许仅上传尾帧
   */
  allowsTailOnly: (model: {
    model_display_name?: string;
    model_name?: string;
    model_id?: string;
  }) => {
    if (!model || !model.model_id) return true;
    return !MODEL_CONSTRAINTS.NO_TAIL_ONLY_MODEL_IDS.includes(model.model_id as any);
  },

  /**
   * 检查模型是否支持首帧功能
   * @param _model 模型信息对象
   * @returns 是否支持首帧功能
   */
  supportsFirstFrame: (_model: {
    model_display_name?: string;
    model_name?: string;
    model_id?: string;
  }) => {
    // 目前所有模型都支持首帧功能
    return true;
  },

  /**
   * 检查模型是否支持参考图功能
   * @param _model 模型信息对象
   * @returns 是否支持参考图功能
   */
  supportsReferenceImage: (_model: {
    model_display_name?: string;
    model_name?: string;
    model_id?: string;
  }) => {
    // 目前所有模型都支持参考图功能
    return true;
  },

  /**
   * 检查模型是否仅支持5s时长
   * @param model 模型信息对象
   * @returns 是否仅支持5s时长
   */
  isOnly5sModel: (model: {
    model_display_name?: string;
    model_name?: string;
    model_id?: string;
  }) => {
    if (!model || !model.model_id) return false;
    return MODEL_CONSTRAINTS.ONLY_5S_MODELS.includes(model.model_id as any);
  },

  /**
   * 检查模型是否仅支持图生视频
   * @param model 模型信息对象
   * @returns 是否仅支持图生视频
   */
  isImageToVideoOnly: (model: {
    model_display_name?: string;
    model_name?: string;
    model_id?: string;
  }) => {
    if (!model || !model.model_id) return false;
    return MODEL_CONSTRAINTS.IMAGE_TO_VIDEO_ONLY_MODELS.includes(model.model_id as any);
  },

  /**
   * 检查模型是否支持10s时长
   * @param model 模型信息对象
   * @returns 是否支持10s时长
   */
  supports10s: (model: { model_display_name?: string; model_name?: string; model_id?: string }) => {
    if (!model || !model.model_id) return true;

    // 检查是否在不支持10s的模型ID列表中
    return !MODEL_CONSTRAINTS.NO_10S_MODEL_IDS.includes(model.model_id as any);
  },

  /**
   * 检查模型是否支持5s时长
   * @param model 模型信息对象
   * @returns 是否支持5s时长
   */
  supports5s: (model: { model_display_name?: string; model_name?: string; model_id?: string }) => {
    if (!model || !model.model_id) return true;
    return true;

    // 海螺02不支持5s时长
    // return model.model_id !== VIDEO_MODEL_IDS.HAILUO_02;
  },
} as const;
